import React, { useCallback, useState } from 'react'
import { Card, Empty, Table } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import type { CarrierSuccessRateDto } from '../../../../api/dto/get-payment-bot-dashboard.dto'
import { usePaymentBotCarrierColumns } from './hooks/usePaymentBotCarrierColumns'
import {
  ResizableTitle,
  withResizableColumns,
} from '../../../shared/ResizableTitle'

const DEFAULT_COL_WIDTHS: Record<string, number> = {
  name: 220,
  type: 110,
  totalBenefits: 110,
  successFailed: 160,
  firstAttemptRate: 150,
  eventualRate: 130,
  momPaidDelta: 130,
  avgTime: 150,
}

interface PaymentBotCarrierTableProps {
  rows: CarrierSuccessRateDto[]
  isFetching: boolean
  onSelectCarrier: (carrierId: number) => void
}

// Per-carrier RPA health table. Row click drills into the carrier-detail view.
// Column widths are stored in state so headers can be dragged to resize.
export function PaymentBotCarrierTable({
  rows,
  isFetching,
  onSelectCarrier,
}: PaymentBotCarrierTableProps) {
  const baseColumns = usePaymentBotCarrierColumns(rows)
  const [colWidths, setColWidths] = useState(DEFAULT_COL_WIDTHS)

  const handleResize = useCallback(
    (key: string) => (width: number) =>
      setColWidths((prev) => ({ ...prev, [key]: width })),
    []
  )

  const columns: ColumnsType<CarrierSuccessRateDto> = withResizableColumns(
    baseColumns,
    colWidths,
    handleResize
  )

  const scrollX = Object.values(colWidths).reduce((a, b) => a + b, 0)

  const handleRowClick = useCallback(
    (carrierId: number) => onSelectCarrier(carrierId),
    [onSelectCarrier]
  )

  return (
    <Card bodyStyle={{ padding: 16 }}>
      <div data-testid="process-health-table">
        <Table<CarrierSuccessRateDto>
          rowKey="carrierId"
          columns={columns}
          dataSource={rows}
          pagination={false}
          loading={isFetching}
          scroll={{ x: scrollX, y: 600 }}
          components={{ header: { cell: ResizableTitle } }}
          locale={{
            emptyText: (
              <Empty description="No payment bot activity for this month." />
            ),
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record.carrierId),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </Card>
  )
}
